import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatCard } from "@/components/shared/StatCard";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { inr, pct } from "@/lib/format";
import { DEFAULT_INCENTIVE } from "@/services/configStore";
import { getClusters, getIncentives } from "@/services/ccApi";
import { computeProMetrics, withIncentives, clusterAggregate } from "@/lib/perf";

const rate = (v) => <span className={v >= 80 ? "text-emerald-600" : v >= 50 ? "text-amber-600" : "text-red-600"}>{v}%</span>;

export default function Performance() {
  const pros = useCollection("professionals");
  const leads = useCollection("lead_distributions");
  const cons = useCollection("consultations");
  const projects = useCollection("projects");
  const [clusters, setClusters] = useState([]);
  const [rules, setRules] = useState(DEFAULT_INCENTIVE);
  useEffect(() => {
    getClusters().then(setClusters).catch(() => {});
    getIncentives().then((r) => setRules({ ...DEFAULT_INCENTIVE, ...r })).catch(() => {});
  }, []);

  const [tab, setTab] = useState("pros");
  const [cluster, setCluster] = useState("all");
  const [q, setQ] = useState("");

  const metrics = useMemo(() => withIncentives(computeProMetrics(pros, leads, cons, projects), clusters, rules), [pros, leads, cons, projects, clusters, rules]);
  const clusterRows = useMemo(() => clusterAggregate(metrics), [metrics]);

  const proRows = metrics
    .filter((m) => cluster === "all" || m.cluster === cluster || (cluster === "Unassigned" && !m.cluster))
    .filter((m) => !q || (m.name + m.category + m.city).toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => b.score - a.score);

  const totals = useMemo(() => ({
    floated: metrics.reduce((s, m) => s + m.floated, 0),
    responded: metrics.reduce((s, m) => s + m.responded, 0),
    confirmed: metrics.reduce((s, m) => s + m.confirmed, 0),
    promise: metrics.reduce((s, m) => s + m.promise, 0),
    incentive: metrics.reduce((s, m) => s + m.incentive, 0),
  }), [metrics]);

  return (
    <div>
      <PageHeader title="Performance" subtitle="Cluster & professional performance computed live from leads, consultations and projects — response rate, visits, delays, Brugo Promise vs normal, and the incentive each pro earns from your rules." />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard testId="perf-kpi-floated" index={0} label="Leads Floated" value={String(totals.floated)} icon="Radar" accent="indigo" />
        <StatCard testId="perf-kpi-rate" index={1} label="Response Rate" value={`${pct(totals.responded, totals.floated)}%`} icon="Percent" accent="cyan" />
        <StatCard testId="perf-kpi-visits" index={2} label="Confirmed Visits" value={String(totals.confirmed)} icon="MapPinCheck" accent="emerald" />
        <StatCard testId="perf-kpi-promise" index={3} label="Promise Projects" value={String(totals.promise)} icon="ShieldCheck" accent="amber" />
        <StatCard testId="perf-kpi-incentive" index={4} label="Total Incentive" value={inr(totals.incentive)} icon="Award" accent="primary" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="pros" data-testid="perf-tab-pros">By Professional</TabsTrigger>
          <TabsTrigger value="clusters" data-testid="perf-tab-clusters">By Cluster</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "pros" ? (
        <Card>
          <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-sm min-w-[180px]"><Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input data-testid="perf-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search professional…" className="pl-9" /></div>
            <Select value={cluster} onValueChange={setCluster}>
              <SelectTrigger className="w-52" data-testid="perf-cluster-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clusters</SelectItem>
                <SelectItem value="Unassigned">Unassigned</SelectItem>
                {clusters.map((c) => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Professional</TableHead><TableHead>Cluster</TableHead><TableHead>Floated</TableHead><TableHead>Resp.</TableHead><TableHead>Rate</TableHead>
                <TableHead>Visits ✓/✕</TableHead><TableHead>Promise</TableHead><TableHead>Normal</TableHead><TableHead>Delayed</TableHead><TableHead>Done</TableHead><TableHead>Incentive</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {proRows.map((m) => (
                  <TableRow key={m.id} data-testid={`perf-row-${m.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-foreground">{m.name}</span>
                        <EditRecordButton path="professionals" id={m.id} className="h-6 w-6" />
                        {m.isTop && <span title="Top pro in cluster"><Icons.Crown className="h-3.5 w-3.5 text-amber-500" /></span>}
                        {m.isLead && <span title="Cluster lead"><Icons.Star className="h-3.5 w-3.5 text-primary" /></span>}
                      </div>
                      <p className="text-[11px] text-muted-foreground">{m.category || "—"} · {m.city || "—"}</p>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.cluster || "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{m.floated}</TableCell>
                    <TableCell className="font-mono text-sm">{m.responded}</TableCell>
                    <TableCell className="font-mono text-sm">{rate(m.responseRate)}</TableCell>
                    <TableCell className="font-mono text-sm"><span className="text-emerald-600">{m.confirmed}</span>/<span className="text-red-600">{m.cancelled}</span></TableCell>
                    <TableCell className="font-mono text-sm">{m.promise}</TableCell>
                    <TableCell className="font-mono text-sm">{m.normal}</TableCell>
                    <TableCell className="font-mono text-sm text-red-600">{m.delayed}</TableCell>
                    <TableCell className="font-mono text-sm">{m.completed}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-foreground">{inr(m.incentive)}</TableCell>
                  </TableRow>
                ))}
                {proRows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground">No professionals.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Cluster</TableHead><TableHead>Pros</TableHead><TableHead>Floated</TableHead><TableHead>Responded</TableHead><TableHead>Rate</TableHead>
                <TableHead>Visits ✓/✕</TableHead><TableHead>Promise</TableHead><TableHead>Normal</TableHead><TableHead>Delayed</TableHead><TableHead>Done</TableHead><TableHead>Incentive</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {clusterRows.map((c) => (
                  <TableRow key={c.cluster} data-testid={`perf-cluster-${c.cluster}`}>
                    <TableCell className="font-medium text-foreground">{c.cluster}</TableCell>
                    <TableCell className="font-mono text-sm">{c.pros}</TableCell>
                    <TableCell className="font-mono text-sm">{c.floated}</TableCell>
                    <TableCell className="font-mono text-sm">{c.responded}</TableCell>
                    <TableCell className="font-mono text-sm">{rate(c.responseRate)}</TableCell>
                    <TableCell className="font-mono text-sm"><span className="text-emerald-600">{c.confirmed}</span>/<span className="text-red-600">{c.cancelled}</span></TableCell>
                    <TableCell className="font-mono text-sm">{c.promise}</TableCell>
                    <TableCell className="font-mono text-sm">{c.normal}</TableCell>
                    <TableCell className="font-mono text-sm text-red-600">{c.delayed}</TableCell>
                    <TableCell className="font-mono text-sm">{c.completed}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-foreground">{inr(c.incentive)}</TableCell>
                  </TableRow>
                ))}
                {clusterRows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground">No data.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
